import { useEffect, useState, type ComponentProps } from "react"
import { format } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"

export type UptimeDashboardLiveIndicatorProps = ComponentProps<"div"> & {
  // True while Apollo's polling refetch is in flight. Drives the
  // pulsing dot so passers-by can tell the wall display is live.
  loading: boolean
  // The wall-clock time the last successful refetch landed. We
  // render this as "Updated 4 s ago" and re-derive every second.
  lastUpdatedAt: Date | null
}

// "Updated 4 s ago" pip in the dashboard header. Polls a 1s ticker
// internally so the relative timestamp counts up between Apollo
// refetches without forcing the parent to re-render every second.
function UptimeDashboardLiveIndicator(
  props: UptimeDashboardLiveIndicatorProps,
) {
  const { loading, lastUpdatedAt, className, title, ...divProps } = props

  // Re-render once a second so "Updated 7 s ago" actually counts up
  // even though the underlying query only refetches every 15 s.
  // The state value itself is intentionally unread -- we only need
  // the bump from the setter to trigger the re-render -- so we hold
  // it as `now` and reuse it as the "current wall-clock" reference
  // below.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const ageMs = lastUpdatedAt ? now - lastUpdatedAt.getTime() : null
  const ageLabel =
    ageMs == null
      ? "—"
      : ageMs < 1000
        ? "just now"
        : ageMs < 60_000
          ? `${Math.floor(ageMs / 1000)} s ago`
          : `${Math.floor(ageMs / 60_000)} min ago`

  return (
    <div
      className={cn(
        "uptime-dashboard-live",
        loading && "uptime-dashboard-live-active",
        className,
      )}
      title={title ?? (lastUpdatedAt ? format(lastUpdatedAt, "HH:mm") : "")}
      {...divProps}
    >
      <span className="uptime-dashboard-live-dot" />
      {loading ? "Updating…" : `Updated ${ageLabel}`}
    </div>
  )
}

export default UptimeDashboardLiveIndicator
